/**
 * Stilisierter Grundriss-Thumb für die Studio-Dashboard-Cards (V2).
 *
 * Quelle: Handoff-Bundle bidi_takeoff-handoff/project/icons-and-plan.jsx
 * (FloorPlan-Komponente, vereinfacht). Das ist KEINE echte Plan-Vorschau —
 * es ist ein dekoratives Architekten-Plan-Pattern, damit jede Card visuell
 * an ein Bauwerk erinnert. Die echte Run-Vorschau (Plan-Bitmap aus
 * <runDir>/source.pdf) kommt in einem späteren Sprint, wenn wir den
 * Bitmap-Endpoint als Thumbnail-Resource exposen.
 *
 * Ein deterministischer Hash auf der runId variiert die Layout-Cluster, damit
 * die Cards nicht alle identisch aussehen.
 */

import { useMemo } from "react";

interface Props {
  runId: string;
  /** Skaliert das SVG (1 = full, 0.6 = compact für 88×88 Continue-Card-Thumb). */
  scale?: number;
  /** Wenn true: ein Raum wird ember-orange hervorgehoben (für Continue-Card). */
  highlight?: boolean;
}

const ROOM_COLORS = [
  "var(--bd-rooms-schlafen)",
  "var(--bd-rooms-bad)",
  "var(--bd-rooms-wohnen)",
  "var(--bd-rooms-flur)",
  "var(--bd-rooms-technik)",
  "var(--bd-rooms-treppenhaus)",
];

interface Room {
  x: number;
  y: number;
  w: number;
  h: number;
  cat: number;
}

export function PlanThumb({ runId, scale = 1, highlight = false }: Props) {
  const rooms = useMemo<Room[]>(() => layoutForRun(runId), [runId]);

  return (
    <svg
      viewBox="0 0 720 540"
      style={{
        width: "100%",
        height: "100%",
        display: "block",
        transform: scale !== 1 ? `scale(${scale})` : undefined,
        transformOrigin: "center center",
      }}
      preserveAspectRatio="xMidYMid meet"
    >
      <defs>
        <pattern id="bd-thumb-grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path
            d="M20 0H0V20"
            fill="none"
            stroke="var(--bd-plan-grid)"
            strokeWidth="0.5"
          />
        </pattern>
      </defs>
      <rect x="0" y="0" width="720" height="540" fill="var(--bd-plan-bg)" />
      <rect x="0" y="0" width="720" height="540" fill="url(#bd-thumb-grid)" />

      {/* Plot boundary (gestrichelt) */}
      <rect
        x="50"
        y="60"
        width="620"
        height="420"
        fill="none"
        stroke="var(--bd-plan-ink)"
        strokeOpacity="0.5"
        strokeWidth="1"
        strokeDasharray="6 4"
      />

      {/* Building outline */}
      <rect
        x="155"
        y="75"
        width="405"
        height="380"
        fill="none"
        stroke="var(--bd-plan-ink)"
        strokeWidth="2"
      />

      {/* Inner walls hint */}
      <path
        d="M155 160 H560 M155 240 H560 M155 300 H560 M155 370 H560 M250 75 V455 M320 75 V455 M390 75 V455 M460 75 V455"
        fill="none"
        stroke="var(--bd-plan-ink)"
        strokeOpacity="0.35"
        strokeWidth="0.6"
      />

      {/* Rooms (deterministisch aus runId) */}
      {rooms.map((r, i) => (
        <rect
          key={i}
          x={r.x}
          y={r.y}
          width={r.w}
          height={r.h}
          fill={
            highlight && i === 0
              ? "color-mix(in oklab, var(--bd-ember) 22%, transparent)"
              : ROOM_COLORS[r.cat % ROOM_COLORS.length]
          }
          fillOpacity={highlight && i === 0 ? 1 : 0.55}
          stroke={
            highlight && i === 0 ? "var(--bd-ember)" : "var(--bd-plan-ink)"
          }
          strokeOpacity={highlight && i === 0 ? 1 : 0.55}
          strokeWidth={highlight && i === 0 ? 1.5 : 0.6}
        />
      ))}

      {/* Compass */}
      <g transform="translate(640,460)">
        <circle
          r="14"
          fill="none"
          stroke="var(--bd-plan-ink)"
          strokeOpacity="0.5"
          strokeWidth="0.8"
        />
        <path
          d="M0,-12 L4,0 L0,12 L-4,0Z"
          fill="var(--bd-plan-ink)"
          fillOpacity="0.7"
        />
      </g>
    </svg>
  );
}

/**
 * Erzeugt deterministisch ein Layout aus 6-12 Räumen aus der runId. Damit
 * sehen Cards mit derselben runId immer gleich aus, aber unterschiedliche
 * runIds bekommen unterschiedliche Cluster-Aufteilungen.
 */
function layoutForRun(runId: string): Room[] {
  const hash = simpleHash(runId);
  const seed = (s: number) => {
    // mulberry32-ish
    let t = (hash + s * 2654435761) | 0;
    t = (t ^ (t >>> 15)) * 1597334677;
    t = (t ^ (t >>> 15)) * 1597334677;
    return ((t ^ (t >>> 15)) >>> 0) / 4294967296;
  };

  // Grid 5×4 inside building outline (155,75 — 560,455 = 405×380).
  const cells: Array<{ col: number; row: number }> = [];
  for (let row = 0; row < 4; row++) {
    for (let col = 0; col < 5; col++) {
      cells.push({ col, row });
    }
  }
  // Pick 8-12 cells deterministically.
  const count = 8 + Math.floor(seed(0) * 5);
  const picked: Room[] = [];
  const used = new Set<number>();
  for (let i = 0; i < count; i++) {
    let idx = Math.floor(seed(i + 1) * cells.length);
    while (used.has(idx)) idx = (idx + 1) % cells.length;
    used.add(idx);
    const { col, row } = cells[idx];
    const w = 81 + Math.floor(seed(i + 100) * 24);
    const h = 90 + Math.floor(seed(i + 200) * 20);
    picked.push({
      x: 155 + col * 81,
      y: 75 + row * 95,
      w: Math.min(w, 560 - (155 + col * 81)),
      h: Math.min(h, 455 - (75 + row * 95)),
      cat: Math.floor(seed(i + 300) * ROOM_COLORS.length),
    });
  }
  return picked;
}

function simpleHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return h | 0;
}
